import React, { useState, useEffect } from 'react'
import { Send, Square } from 'lucide-react'
import { useTranslation } from '@/i18n'

// 定义sence数据类型
interface SenceItem {
  id: number;
  desc: string; // 对应后端的desc字段
  background: string; // 对应后端的prompt字段
  dialogue: string;   // 对应后端的dialogue字段
  images: number[];
  videoUrl: string;
  audioUrl: string;
}

const VideoAnalysisPage: React.FC = () => {
  const { t } = useTranslation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isSent, setIsSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [inputContent, setInputContent] = useState('')
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  // 真实的senceList状态
  const [senceList, setSenceList] = useState<SenceItem[]>([])

  // 监听滚动事件
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // 发送请求
  const handleSendRequest = () => {
    if (!inputContent.trim() || isLoading) return

    setIsLoading(true)
    setIsSent(true)
    setSenceList([])

    // 这里应该替换为真实的API调用
    // 模拟流处理
    simulateStreamResponse()
  }

  // 模拟流响应处理
  const simulateStreamResponse = () => {
    // 模拟响应数据
    const mockResponse = `**title**:瑞士阿尔卑斯山清晨徒步与日出美景分镜
---
## sence
**desc**:清晨的山间木屋内，旅行博主坐在窗边，桌上放着登山杖、背包和一杯冒着热气的咖啡，博主正在整理装备。窗外是朦胧的雪山轮廓，温暖的晨光透过窗户洒入室内。镜头缓慢从左向右平移，展现木屋的温馨氛围。
**prompt**:(1920x1080, 24fps, 6s, best quality, masterpiece), cozy mountain cabin interior, travel blogger sitting by window, hiking poles and backpack on table, steaming coffee cup, warm morning light through window, distant snow-capped mountains outside, soft natural lighting, slow pan right, cinematic style
**dialogue**:清晨五点的阿尔卑斯山，木屋里弥漫着咖啡的香气，准备迎接今天的日出徒步。
---
## sence
**desc**:博主背起背包走出木屋，站在门口深深呼吸清晨的空气，呼出的气息在冷空气中形成白雾。镜头从室内跟随博主移动至室外，展现博主站在木屋门廊上的身影。
**prompt**:(1920x1080, 24fps, 5s, best quality, masterpiece), travel blogger stepping out of cabin, wearing hiking gear, breathing deeply in cold morning air, visible breath vapor, wooden porch, maintain the same style as the previous shot, slow dolly out
**dialogue**:推开木门，清新的山间空气扑面而来，这是阿尔卑斯山独有的清晨味道。
`

    // 模拟流解析
    let currentScene: Partial<SenceItem> = {}
    let scenes: SenceItem[] = []
    let currentField: keyof SenceItem | null = null

    // 模拟逐行接收
    const simulateStreaming = () => {
      const lines = mockResponse.split('\n')

      let currentIndex = 0

      const interval = setInterval(() => {
        if (currentIndex < lines.length) {
          const line = lines[currentIndex]
          currentIndex++

          // 解析当前行
          if (line === '## sence') {
            if (Object.keys(currentScene).length > 0) {
              scenes.push({
                ...(currentScene as any),
                id: scenes.length + 1,
                images: [1],
                videoUrl: '#',
                audioUrl: '#'
              })
            }
            currentScene = {}
            currentField = null
          } else if (line.startsWith('**desc**:')) {
            currentScene.desc = line.substring('**desc**:'.length)
            currentField = 'desc'
          } else if (line.startsWith('**prompt**:')) {
            currentScene.background = line.substring('**prompt**:'.length)
            currentField = 'background'
          } else if (line.startsWith('**dialogue**:')) {
            currentScene.dialogue = line.substring('**dialogue**:'.length)
            currentField = 'dialogue'
          } else if (line === '---') {
            // 结束当前场景
            if (Object.keys(currentScene).length > 0) {
              scenes.push({
                ...(currentScene as any),
                id: scenes.length + 1,
                images: [1],
                videoUrl: '#',
                audioUrl: '#'
              })
              currentScene = {}
            }
          } else if (currentField && line) {
            // 处理多行内容
            if (currentField === 'desc') {
              currentScene.desc += ' ' + line
            } else if (currentField === 'background') {
              currentScene.background += ' ' + line
            } else if (currentField === 'dialogue') {
              currentScene.dialogue += ' ' + line
            }
          }

          // 更新场景列表
          setSenceList([...scenes])
        } else {
          // 完成加载
          clearInterval(interval)
          setIsLoading(false)
        }
      }, 200)
    }

    simulateStreaming()
  }

  // 恢复输入状态
  const restoreInputState = () => {
    if (isSent && textareaRef.current) {
      setIsSent(false)
      textareaRef.current.focus()
      // 重置状态
      setSenceList([])
    }
  }

  // 自适应文本区域高度
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target
    textarea.style.height = 'auto' // 重置高度
    textarea.style.height = `${textarea.scrollHeight}px` // 设置新高度
  }

  return (
    <div className="w-screen h-screen bg-gradient-to-br from-gray-950 to-gray-900">
      {/* 分镜区域 senceContainer */}
      <div className="absolute left-[160px] right-0 top-[100px] bottom-[120px] overflow-y-auto pb-32 scrollbar-hidden">
        {/* 分镜列表 senceList */}
        <div className="flex flex-row h-full gap-4 overflow-y-auto">
          {senceList.map(item => (
            <div key={item.id} className="bg-gray-800/90 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-4 min-w-[240px] max-w-[300px] h-[320px] flex flex-col justify-between shadow-xl">
              {/* 分镜顶部信息栏 senceInfoBar */}
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-bold text-white">分镜 {item.id}</div>
                <div className="w-3 h-3 rounded-full bg-gray-600">
                  {/* 未实现的状态指示器 */}
                </div>
              </div>

              {/* 分镜预览图 previewImage */}
              <div className="w-full h-[100px] bg-gray-700/50 rounded-xl flex items-center justify-center mb-4">
                {/* 模拟图片 */}
                <div className="text-gray-400">预览图</div>
              </div>

              {/* 分镜描述 senceDescription */}
              <div className="flex-1 overflow-y-auto mb-3">
                <div className="text-xs font-medium text-gray-400 mb-1">分镜描述</div>
                <div className="text-sm text-gray-300 line-clamp-4">
                  {item.desc}
                </div>
              </div>

              {/* 分镜右侧控制面板 */}
              <div className="flex items-center gap-2 mt-2">
                {/* 图片切换按钮 */}
                <button
                  className="w-full flex-1 px-4 py-2 bg-gray-700/50 rounded-xl text-gray-300 hover:text-white hover:bg-gray-700/70 transition-all"
                  onClick={() => alert('切换图片')}
                >
                  <div className="text-xs">图片</div>
                </button>
                {/* 音频切换按钮 */}
                <button
                  className="w-full flex-1 px-4 py-2 bg-gray-700/50 rounded-xl text-gray-300 hover:text-white hover:bg-gray-700/70 transition-all"
                  onClick={() => alert('切换音频')}
                >
                  <div className="text-xs">音频</div>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 分镜底部工具栏  senceBottomBar */}
      <div className="absolute left-[180px] right-[180px] bottom-24 p-4 rounded-2xl border border-gray-700/50 bg-gray-850/90 backdrop-blur-md shadow-2xl flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* 分镜信息 */}
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium text-gray-400">分镜：</div>
            <div className="text-sm font-bold text-white">{senceList.length} / 0</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium text-gray-400">时长：</div>
            <div className="text-sm font-bold text-white">00:00:00 / 00:00:00</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium text-gray-400">状态：</div>
            <div className="text-sm font-bold text-white">分析中</div>
          </div>
        </div>

        {/* 分析按钮 */}
        <div className="flex items-center gap-3">
          <button
            className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl px-6 py-3 text-white font-medium hover:shadow-lg transition-all"
            onClick={() => alert('重新分析')}
          >
            重新分析
          </button>
          <button
            className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl px-6 py-3 text-white font-medium hover:shadow-lg transition-all"
            onClick={() => alert('保存分析')}
          >
            保存分析
          </button>
        </div>
      </div>

      {/* 底部输入栏 inputBar */}
      <div
        className={`fixed bottom-[20px] left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out ${(isScrolled || isSent) ? 'w-[24%] cursor-pointer' : 'w-[34%]'
          }`}
        onClick={restoreInputState}
      >
        {/* 输入框容器 */}
        <div
          className={`relative bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-md rounded-2xl p-4 shadow-2xl transition-all duration-300 ${(isScrolled || isSent) ? 'pb-2' : 'pb-4'
            }`}
          style={{ opacity: 1 }}
        >
          {/* 文本输入框和发送按钮容器 */}
          <div className="relative">
            <textarea
              ref={textareaRef}
              className={`w-full bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-xl px-6 py-3 pr-20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none mb-4 ${isSent ? 'scrollbar-hidden' : ''}`}
              placeholder={t('home.analysisPlaceholder')}
              rows={1}
              onChange={(e) => {
                handleTextareaChange(e)
                setInputContent(e.target.value)
              }}
              value={inputContent}
              disabled={isSent || isLoading}
              style={{
                minHeight: (isScrolled || isSent) ? '48px' : '100px',
                maxHeight: (isScrolled || isSent) ? '48px' : '120px'
              }} // 缩小时1行，未缩小时3行
              onClick={restoreInputState} // 点击恢复状态
            />

            {/* 圆形发送按钮 */}
            <button
              className={`absolute right-4 bottom-8 w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white shadow-lg hover:shadow-xl transition-all duration-300 scale-105 ${isScrolled ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
              onClick={handleSendRequest}
              disabled={isLoading || !inputContent.trim()}
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : isSent ? (
                <Square className="w-4 h-4" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* 配置选项 - 比例、分辨率、音色 */}
          <div
            className={`grid grid-cols-1 md:grid-cols-[3fr_3fr_3fr_2fr] gap-4 transition-all duration-300 ${(isScrolled || isSent) ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-40'
              }`}
          >
            {/* 比例选择 */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-300 mb-1">比例</label>
              <select
                className="w-full bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
                disabled={isSent}
              >
                <option value="16:9">16:9 (横屏)</option>
                <option value="9:16">9:16 (竖屏)</option>
                <option value="4:3">4:3</option>
                <option value="1:1">1:1 (方形)</option>
              </select>
              <div className="absolute right-3 top-10 pointer-events-none text-gray-400">▼</div>
            </div>

            {/* 分辨率选择 */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-300 mb-1">分辨率</label>
              <select
                className="w-full bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
                disabled={isSent}
              >
                <option value="4k">4K (3840×2160)</option>
                <option value="1080p">1080P (1920×1080)</option>
                <option value="720p">720P (1280×720)</option>
                <option value="480p">480P (854×480)</option>
              </select>
              <div className="absolute right-3 top-10 pointer-events-none text-gray-400">▼</div>
            </div>

            {/* 分析类型选择 */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-300 mb-1">分析类型</label>
              <select
                className="w-full bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
                disabled={isSent}
              >
                <option value="default">默认分析</option>
                <option value="detailed">详细分析</option>
                <option value="fast">快速分析</option>
              </select>
              <div className="absolute right-3 top-10 pointer-events-none text-gray-400">▼</div>
            </div>
            <div className="relative">
              <label className="block text-xs font-medium text-gray-300 mb-1">时长限制</label>
              <select
                className="w-full bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
                disabled={isSent}
              >
                <option value="auto">自动</option>
                <option value="30">30秒</option>
                <option value="60">60秒</option>
                <option value="120">120秒</option>
                <option value="180">180秒</option>
              </select>
              <div className="absolute right-3 top-10 pointer-events-none text-gray-400">▼</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VideoAnalysisPage